/* eslint-disable react/display-name */
import {
  Flex,
  Box,
  Button,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Checkbox,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useDisclosure,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useMemo, useState, useReducer } from 'react';

import {
  GetAssignedReceivablesForLoanProductQueryGQLType,
  AssignmentConfirmationJobGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  frozenHeaderRowStyling,
  frozenTableStyling,
} from 'packages/common-frontend/styles';

const constructColumnDefinition = ({
  columnId,
  headerText,
  accessorFn,
}: {
  columnId: string;
  headerText: string;
  accessorFn: (row: any) => string;
}): ColumnDef<any, any> => {
  return {
    id: columnId,
    header: () => {
      return (
        <Typography variant="base" fontWeight="medium">
          {headerText}
        </Typography>
      );
    },
    accessorFn,
    cell: ({ getValue }) => {
      return <Typography variant="base-muted">{getValue()}</Typography>;
    },
    meta: {
      style: { padding: '16px' },
    },
  };
};

export const BulkActionsButton = ({
  onConfirm,
  rowsSelectedCount,
  jobInProgress,
  creditFacilityName,
}: {
  onConfirm: (
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>,
  ) => void;
  rowsSelectedCount: number;
  jobInProgress: boolean;
  creditFacilityName: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef(null);

  const [jobType, setJobType] = useState<
    Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>
  >('repurchase');

  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          leftIcon={
            rowsSelectedCount > 0 ? (
              <Box
                background={colors.gray[100]}
                borderRadius="4px"
                padding="4px 8px"
              >
                {rowsSelectedCount}
              </Box>
            ) : undefined
          }
          rightIcon={<Icon icon="chevron-down" />}
          variant="outline"
          colorScheme="aurora"
          isDisabled={rowsSelectedCount === 0 || jobInProgress}
          width="fit-content"
        >
          Bulk Action
        </MenuButton>
        <MenuList zIndex={10}>
          <MenuItem
            onClick={() => {
              setJobType('repurchase');
              onOpen();
            }}
          >
            Repurchase to Balance Sheet
          </MenuItem>
          <MenuItem
            onClick={() => {
              setJobType('transfer');
              onOpen();
            }}
          >
            Transfer to {creditFacilityName}
          </MenuItem>
        </MenuList>
      </Menu>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader
              fontSize="24px"
              fontWeight="bold"
              p="40px 40px 16px"
            >
              Confirm {_.capitalize(String(jobType))}
            </AlertDialogHeader>

            <AlertDialogBody p="8px 40px 16px">
              <Typography variant="base-muted">
                Are you sure you want to {jobType} the selected receivables{' '}
                {jobType === 'transfer'
                  ? `to ${creditFacilityName}`
                  : 'and move them to the balance sheet'}
                ?
              </Typography>
            </AlertDialogBody>

            <AlertDialogFooter p="16px 40px 40px">
              <Button
                variant="outline"
                colorScheme="aurora"
                ref={cancelRef.current}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="aurora"
                onClick={async () => {
                  onConfirm(jobType);
                  onClose();
                }}
                ml="32px"
              >
                Confirm {_.capitalize(String(jobType))}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export const ReceivableReconciliationTable = ({
  receivables,
  creditFacilityName,
  initiateReassignmentConfirmationJob,
  jobInProgress,
}: {
  receivables: GetAssignedReceivablesForLoanProductQueryGQLType['assignedReceivablesForLoanProduct'];
  creditFacilityName: string;
  initiateReassignmentConfirmationJob: ({
    rowSelectionData,
    jobType,
  }: {
    rowSelectionData: any;
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
  }) => void;
  jobInProgress: boolean;
}) => {
  const columns: ColumnDef<any, any>[] = [
    constructColumnDefinition({
      columnId: 'externalReceivableId',
      headerText: 'Receivable ID',
      accessorFn: (row) => row.externalReceivableId,
    }),
    constructColumnDefinition({
      columnId: 'signaledCreditFacilityName',
      headerText: 'Loan Tape Assigned Facility',
      accessorFn: () => creditFacilityName,
    }),
    constructColumnDefinition({
      columnId: 'assignedCreditFacilityName',
      headerText: 'Finley Assigned Facility',
      accessorFn: (row) => row.creditFacility.name,
    }),
  ];

  const [rowsSelectedCount, setRowsSelectedCount] = useState<number>(0);

  const rowSelectionReducer = (state: any, action: any) => {
    switch (action.type) {
      case 'TOGGLE_ROW': {
        const newState = { ...state };
        setRowsSelectedCount(
          (oldCount) => oldCount + (state[action.rowId] ? -1 : 1),
        );
        if (newState[action.rowId]) {
          delete newState[action.rowId];
        } else {
          newState[action.rowId] = true;
        }
        return newState;
      }
      case 'TOGGLE_ALL': {
        if (rowsSelectedCount < receivables.length) {
          setRowsSelectedCount(receivables.length);
          return action.allRowIds.reduce(
            (acc: { [id: string]: boolean }, rowId: string) => {
              // eslint-disable-next-line no-param-reassign
              acc[rowId] = true;

              return acc;
            },
            {},
          );
        }
        setRowsSelectedCount(0);
        return {};
      }

      default:
        return state;
    }
  };

  const [rowSelection, dispatchRowSelection] = useReducer(
    rowSelectionReducer,
    {},
  );

  const table = useReactTable({
    data: receivables,
    columns,
    state: {
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    getRowId: (row) => row.externalReceivableId,
    onRowSelectionChange: dispatchRowSelection,
  });

  const allRowIds = useMemo(
    () => table.getRowModel().rows.map((row) => row.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [receivables],
  );

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <BulkActionsButton
          rowsSelectedCount={rowsSelectedCount}
          jobInProgress={jobInProgress}
          onConfirm={(
            jobType: Omit<
              AssignmentConfirmationJobGQLType['jobType'],
              'assignment'
            >,
          ) => {
            initiateReassignmentConfirmationJob({
              rowSelectionData: rowSelection,
              jobType,
            });
          }}
          creditFacilityName={creditFacilityName}
        />
        <Typography fontWeight="bold">
          Receivable Count: {allRowIds.length}
        </Typography>
      </Flex>
      <Box
        borderWidth="1px"
        borderColor={colors.gray[100]}
        overflowX="auto"
        overflowY="auto"
        position="relative"
        maxHeight="50vh"
        m="24px 0px"
      >
        <Table style={frozenTableStyling}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      borderBottomWidth="thin"
                      position="sticky"
                      top="0"
                      backgroundColor={colors.background[100]}
                      minW={header.column.getSize()}
                      maxW={header.column.getSize()}
                    >
                      <Flex alignItems="center">
                        {header.id === 'externalReceivableId' && (
                          <Checkbox
                            mr="16px"
                            isChecked={table.getIsAllRowsSelected()}
                            isIndeterminate={table.getIsSomeRowsSelected()}
                            onChange={() =>
                              dispatchRowSelection({
                                type: 'TOGGLE_ALL',
                                allRowIds,
                              })
                            }
                            isDisabled={jobInProgress}
                          />
                        )}
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody backgroundColor="white">
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} padding="8px 16px" height="56px">
                    <Flex alignItems="center">
                      {cell.column.id === 'externalReceivableId' && (
                        <Checkbox
                          mr="16px"
                          isChecked={row.getIsSelected()}
                          onChange={() =>
                            dispatchRowSelection({
                              type: 'TOGGLE_ROW',
                              rowId: row.original.externalReceivableId,
                            })
                          }
                          isDisabled={jobInProgress}
                        />
                      )}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Flex>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
